import { baseApi } from "@/shared/api";
import type { SigningActionResponse } from "@/shared/types";
import { fileResponseHandler, type FileResponse } from "@/shared/utils";
import type {
  GoodsSupplyApiPayload,
  GoodsSupplyResponse,
  GoodsSupplyContract,
  GoodsSupplyListResponse,
} from "../types";

export const goodsSupplyApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    submitGoodsSupply: builder.mutation<GoodsSupplyResponse, GoodsSupplyApiPayload>({
      query: (body) => ({
        url: "/api/legal/goods-supply/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "GoodsSupply" as const, id: "LIST" }],
    }),

    updateGoodsSupply: builder.mutation<GoodsSupplyResponse, { id: number; data: GoodsSupplyApiPayload }>({
      query: ({ id, data }) => ({
        url: `/api/legal/goods-supply/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "GoodsSupply" as const, id },
        { type: "GoodsSupply" as const, id: "LIST" },
      ],
    }),

    getGoodsSupply: builder.query<GoodsSupplyResponse, number>({
      query: (id) => ({
        url: `/api/legal/goods-supply/${id}/`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "GoodsSupply" as const, id }],
    }),

    getGoodsSupplyList: builder.query<GoodsSupplyContract[], void>({
      query: () => ({
        url: "/api/legal/goods-supply/",
        method: "GET",
      }),
      transformResponse: (response: GoodsSupplyListResponse): GoodsSupplyContract[] => response.results,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "GoodsSupply" as const, id })),
              { type: "GoodsSupply" as const, id: "LIST" },
            ]
          : [{ type: "GoodsSupply" as const, id: "LIST" }],
    }),

    submitGoodsSupplyForSigning: builder.mutation<SigningActionResponse, { id: number; cms_signature: string; file_base64: string }>({
      query: ({ id, cms_signature, file_base64 }) => ({
        url: `/api/legal/goods-supply/${id}/submit-for-signing/`,
        method: "POST",
        body: { cms_signature, file_base64 },
        credentials: "include" as const,
      }),
      invalidatesTags: () => [
        { type: "GoodsSupply" as const, id: "LIST" },
      ],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(
          goodsSupplyApi.util.updateQueryData("getGoodsSupply", id, (draft) => {
            draft.signing_status = 1;
          }),
        );
      },
    }),

    downloadGoodsSupplyPreview: builder.mutation<FileResponse, number>({
      query: (id) => ({
        url: `/api/legal/goods-supply/${id}/preview/`,
        method: "GET",
        responseHandler: fileResponseHandler,
      }),
    }),

    deleteGoodsSupply: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/legal/goods-supply/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "GoodsSupply" as const, id },
        { type: "GoodsSupply" as const, id: "LIST" },
      ],
    }),

    revokeGoodsSupply: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/legal/goods-supplies/${id}/revoke/`,
        method: "POST",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "GoodsSupply" as const, id },
        { type: "GoodsSupply" as const, id: "LIST" },
        "PendingActions",
      ],
    }),
  }),
});

export const {
  useSubmitGoodsSupplyMutation,
  useUpdateGoodsSupplyMutation,
  useGetGoodsSupplyQuery,
  useGetGoodsSupplyListQuery,
  useSubmitGoodsSupplyForSigningMutation,
  useDownloadGoodsSupplyPreviewMutation,
  useDeleteGoodsSupplyMutation,
  useRevokeGoodsSupplyMutation,
} = goodsSupplyApi;
